import { RevxMeasureInput } from 'components/RevxMeasureInput';
import { MeasureType } from 'domains/measurementDomain';
import { Form, Input, InputNumber, Select, Space } from 'antd';
import { useForm } from 'antd/lib/form/Form';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { RevxButton } from 'components/RevxButton';
import { RevxSpin } from 'components/RevxSpin';
import { FilterOperatorEnum } from 'domains/appDomain';
import { MatFilterObj } from 'domains/matDomain';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  normalizeFilterObj,
  normalizeToFormFields,
  normalizeValues,
} from 'utils/filter';
import { FilterOperatorSelect } from '../common/FilterOperatorSelect';
import { FormItemForFilterValues } from '../common/FormItemForFilterValues';
import {
  isSingleOperator,
  MARKUP_OPERATORS,
  NUMERIC_OPERATORS,
} from '../common/utils';
import {
  useMeasurementUnit,
  useRefillFilterOnUnitSwitch,
} from 'hooks/useMeasurementUnit';

export const MatFilterForm = () => {
  const [form] = useForm();
  const { tag } = useMeasurementUnit();
  useRefillFilterOnUnitSwitch(form);
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const filterObj = useAppSelector((state) => state.mat.filterObj);
  const loading = useAppSelector((state) => state.loading.effects.mat.getList);
  const [markupOperator, setMarkupOperator] = useState(
    filterObj.markupPct?.operator
  );

  const onFinish = (values: MatFilterObj) => {
    // Tagged here, not at the request: the panel closes and the list re-fetches for every page
    // and sort afterwards, so the unit these numbers were typed in has to travel with them.
    const filterObj = tag(normalizeFilterObj(values));
    dispatch.mat.saveFilter(filterObj);
  };

  useEffect(() => {
    form.setFieldsValue(normalizeToFormFields(filterObj));
    setMarkupOperator(filterObj.markupPct?.operator);
  }, [filterObj, form]);

  const clearFilters = () => {
    dispatch.mat.removeFilter();
    form.resetFields();
  };

  return (
    <RevxSpin spinning={loading}>
      <Form
        layout="vertical"
        form={form}
        onFinish={onFinish}
        autoComplete="off"
        onValuesChange={(values) => {
          console.log('onValues Change', values);
        }}
      >
        <Form.Item label={t('platform.filter.formLabel.id')}>
          <Form.Item
            noStyle
            name={['id', 'operator']}
            initialValue={FilterOperatorEnum.EQUALS}
          >
            <Input hidden />
          </Form.Item>
          <FormItemForFilterValues noStyle name={['id', 'values']}>
            <Input allowClear />
          </FormItemForFilterValues>
        </Form.Item>

        <Form.Item label={t('platform.filter.formLabel.name')}>
          <Form.Item
            noStyle
            name={['name', 'operator']}
            initialValue={FilterOperatorEnum.CONTAINS}
          >
            <Input hidden />
          </Form.Item>
          <FormItemForFilterValues noStyle name={['name', 'values']}>
            <Input allowClear />
          </FormItemForFilterValues>
        </Form.Item>

        <Form.Item label={t('platform.mat.form.itemLabels.isPublic')}>
          <Form.Item
            noStyle
            name={['isPublic', 'operator']}
            initialValue={FilterOperatorEnum.EQUALS}
          >
            <Input hidden />
          </Form.Item>

          <FormItemForFilterValues noStyle name={['isPublic', 'values']}>
            <Select
              getPopupContainer={(e) => {
                return e as HTMLElement;
              }}
            >
              <Select.Option key={'true'} value={'true'}>
                {t('platform.mat.table.columns.public')}
              </Select.Option>
              <Select.Option key={'false'} value={'false'}>
                {t('platform.mat.table.columns.private')}
              </Select.Option>
            </Select>
          </FormItemForFilterValues>
        </Form.Item>

        <Form.Item label={t('platform.filter.formLabel.categoryContains')}>
          <Form.Item
            noStyle
            name={['category', 'operator']}
            initialValue={FilterOperatorEnum.CONTAINS}
          >
            <Input hidden />
          </Form.Item>
          <FormItemForFilterValues noStyle name={['category', 'values']}>
            <Input allowClear />
          </FormItemForFilterValues>
        </Form.Item>

        <Form.Item label={t('platform.filter.formLabel.colorContains')}>
          <Form.Item
            noStyle
            name={['color', 'operator']}
            initialValue={FilterOperatorEnum.CONTAINS}
          >
            <Input hidden />
          </Form.Item>
          <FormItemForFilterValues noStyle name={['color', 'values']}>
            <Input allowClear />
          </FormItemForFilterValues>
        </Form.Item>

        <Form.Item label={t('platform.filter.formLabel.finishContains')}>
          <Form.Item
            noStyle
            name={['finish', 'operator']}
            initialValue={FilterOperatorEnum.CONTAINS}
          >
            <Input hidden />
          </Form.Item>
          <FormItemForFilterValues noStyle name={['finish', 'values']}>
            <Input allowClear />
          </FormItemForFilterValues>
        </Form.Item>

        <Form.Item label={t('platform.filter.formLabel.basisWeightContains')}>
          <Form.Item
            noStyle
            name={['basisWeight', 'operator']}
            initialValue={FilterOperatorEnum.CONTAINS}
          >
            <Input hidden />
          </Form.Item>
          <FormItemForFilterValues noStyle name={['basisWeight', 'values']}>
            <Input allowClear />
          </FormItemForFilterValues>
        </Form.Item>
        <Form.Item
          label={t('platform.filter.formLabel.widthIn')}
          name={['widthIn', 'values']}
          normalize={normalizeValues}
        >
          <RevxMeasureInput
            type={MeasureType.LENGTH}
            addonBefore={
              <Form.Item
                name={['widthIn', 'operator']}
                noStyle
                initialValue={FilterOperatorEnum.EQUALS}
              >
                <FilterOperatorSelect operatorOptions={NUMERIC_OPERATORS} />
              </Form.Item>
            }
          />
        </Form.Item>
        <Form.Item
          label={t('platform.filter.formLabel.lengthIn')}
          name={['lengthIn', 'values']}
          normalize={normalizeValues}
        >
          <RevxMeasureInput
            type={MeasureType.LENGTH}
            addonBefore={
              <Form.Item
                name={['lengthIn', 'operator']}
                noStyle
                initialValue={FilterOperatorEnum.EQUALS}
              >
                <FilterOperatorSelect operatorOptions={NUMERIC_OPERATORS} />
              </Form.Item>
            }
          />
        </Form.Item>

        <Form.Item
          label={t('platform.filter.formLabel.markupPCT')}
          name={['markupPct', 'values']}
          normalize={normalizeValues}
        >
          <InputNumber
            disabled={markupOperator && isSingleOperator(markupOperator)}
            addonBefore={
              <Form.Item
                name={['markupPct', 'operator']}
                noStyle
                initialValue={FilterOperatorEnum.EQUALS}
              >
                <FilterOperatorSelect
                  operatorOptions={MARKUP_OPERATORS}
                  onChange={(val) => {
                    setMarkupOperator(val as FilterOperatorEnum);

                    if (isSingleOperator(val as FilterOperatorEnum)) {
                      form.setFieldsValue({
                        markupPct: {
                          values: [],
                          operator: val,
                        },
                      });
                    }
                  }}
                />
              </Form.Item>
            }
          />
        </Form.Item>

        <Form.Item>
          <Space>
            <RevxButton
              type="primary"
              htmlType="submit"
              brandLightPurple
              className="admin-filter-btn"
            >
              {t('platform.filter.filterBtnLabel')}
            </RevxButton>
            <RevxButton
              className="admin-clear-filter-btn"
              brandGreen
              onClick={() => {
                clearFilters();
              }}
            >
              {t('platform.filter.clearFilterBtnLabel')}
            </RevxButton>
          </Space>
        </Form.Item>
      </Form>
    </RevxSpin>
  );
};
